import React from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import {getImageUrl, handleImageError} from '../utils/imageUtils'
import productImage from '../assets/images/our-product.png';
import userImage from '../assets/images/users_icon.png';

export default function OrderDetailsModal({ show, handleClose, order, moveToNextStep }) {
  const [isUpdating, setIsUpdating] = React.useState(false);

  if (!order) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Pending';
    return new Date(dateString).toLocaleString();
  };

  const handleMoveClick = async () => {
    setIsUpdating(true);
    await moveToNextStep(order);
    setIsUpdating(false);
  };

  return (
    <Modal show={show} onHide={handleClose} className="order-details-modal" size="lg" centered>
      <Modal.Body className="p-4 ">

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2">
            <img
              src={getImageUrl(order.image,userImage)}
              alt="profile"
              className="rounded-circle"
              width="45"
              height="45"
            />
            <h6 className="fw-bold m-0">{order.userName}</h6>
          </div>

          <button
            onClick={handleClose}
            className="border-0 bg-transparent fs-5 fw-bold text-secondary"
            style={{ cursor: "pointer" }}
          >
            ×
          </button>
        </div>

        {/* Order Info */}
        <div className="d-flex justify-content-between text-muted fw-semibold mb-4 flex-wrap gap-3">
          <div>
            <div>Order #</div>
            <div className="text-dark fw-normal">{order.orderId}</div>
          </div>
          <div>
            <div>Order Placed</div>
            <div className="text-dark fw-normal">{formatDate(order.createdOnUtc)}</div>
          </div>
          <div>
            <div>Payment</div>
            <div className="text-dark fw-normal">{order.paymentMethod || 'N/A'}</div>
          </div>
          <div>
            <div>Delivery Address</div>
            <div className="text-dark fw-normal">
              {order.shippingAddress ? (
                <>
                  <Badge className="custom-badge me-1">Home</Badge>
                  {order.shippingAddress.startsWith('http://') || order.shippingAddress.startsWith('https://') || order.shippingAddress.includes('google.com/maps') || order.shippingAddress.includes('maps?q=') ? (
                    <a
                      href={order.shippingAddress}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#1976D2', fontWeight: '600', textDecoration: 'none' }}
                    >
                      📍 View Location on Map
                    </a>
                  ) : (
                    order.shippingAddress
                  )}
                </>
              ) : order.userPhone ? (
                <a
                  href={`https://wa.me/${(order.userPhone || '').replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#25D366', fontWeight: '600', textDecoration: 'none' }}
                >
                  📱 {order.userPhone} (via WhatsApp)
                </a>
              ) : (
                <span className="text-muted">Not provided</span>
              )}
            </div>
          </div>
        </div>

        <hr />

        {/* Product list */}
        {order.products.map((product, idx) => {
          const wheatType = product.attributes?.product?.[0]?.name || product.productName;
          const addons = product.attributes?.subProducts || [];
          const grindingAttrs = product.attributes?.attributes || [];
          return (
            <div
              key={product.orderItemId || idx}
              className="d-flex justify-content-between align-items-start mb-4"
            >
              <div className="d-flex align-items-center gap-3">
                <img
                  src={getImageUrl(product.image, productImage)}
                  onError={(e) => handleImageError(e, productImage)}
                  alt={product.productName}
                  width="50"
                />

                <div>
                  <h6 className="fw-bold mb-1">
                    {product.productName} <span className="text-muted">₹{product.price}</span>
                  </h6>
                  {/* Wheat Type */}
                  {wheatType && wheatType !== product.productName && (
                    <div className="text-muted" style={{ fontSize: '13px', marginBottom: '4px' }}>
                      Wheat: <strong style={{ color: '#5d4037' }}>{wheatType}</strong>
                      {product.attributes?.product?.[0]?.weight && (
                        <span className="text-muted"> · {product.attributes.product[0].weight}</span>
                      )}
                    </div>
                  )}
                  {/* Grinding/Packaging attributes */}
                  {grindingAttrs.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#795548', marginBottom: '4px' }}>
                      {grindingAttrs.map((a, i) => (
                        <span key={i}>{a.name}: <strong>{a.value}</strong>{i < grindingAttrs.length - 1 ? ' · ' : ''}</span>
                      ))}
                    </div>
                  )}
                  {/* Add-ons */}
                  <div className="d-flex flex-wrap gap-2">
                    {addons.map((subProduct, i) => (
                      <span
                        key={subProduct.id || i}
                        className="grain-pill"
                      >
                        {subProduct.name} {subProduct.weight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-end" style={{ fontSize: '13px', color: '#5d4037', fontWeight: '600', minWidth: '60px' }}>
                {product.weight} Kg
              </div>
            </div>
          );
        })}

        <hr />

        {/* Footer */}
        <div className="footer-info">
          <div className="details">
            <div className="text-center">
              <div className="label">Total QTY</div>
              <div className="value">{order.weight}Kg</div>
            </div>
            <div className="text-center">
              <div className="label">Sub Total</div>
              <div className="value">₹{order.totalPrice}</div>
            </div>
            <div className="text-center">
              <div className="label">Payment Info</div>
              <div className="value">{order.paymentStatus}</div>
            </div>
          </div>

          <div className="d-flex gap-3 justify-content-end w-100">
            {order.orderStatus.toLowerCase() !== 'completed' && order.orderStatus.toLowerCase() !== 'cancelled' && (
              <Button
                variant="outline-danger"
                style={{ borderRadius: "8px", fontWeight: "bold", padding: "10px 20px" }}
                disabled={isUpdating}
                onClick={async () => {
                  if (window.confirm("Are you sure you want to cancel this order?")) {
                    setIsUpdating(true);
                    await moveToNextStep(order, 6);
                    setIsUpdating(false);
                  }
                }}
              >
                CANCEL ORDER
              </Button>
            )}

            {order.orderStatus.toLowerCase() !== 'completed' && order.orderStatus.toLowerCase() !== 'cancelled' && (
              <Button
                className="btn-move"
                onClick={handleMoveClick}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <span className="d-flex align-items-center gap-2">
                    <div className="spinner-border spinner-border-sm"></div>
                    Updating...
                  </span>
                ) : order.orderStatus.toLowerCase() === 'pending' ? (
                  "MOVE TO IN PROGRESS"
                ) : order.orderStatus.toLowerCase() === 'processing' ? (
                  "MOVE TO OUT FOR DELIVERY"
                ) : (
                  "MOVE TO COMPLETED"
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}